import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ticket, X, ArrowRight, Calendar } from "lucide-react";

// Bump this when we want to force everyone to see a re-worded banner
// again (e.g. new event / new promo). The dismissal key is version-scoped
// so previously-dismissed viewers see a refreshed banner without us
// clearing anything server-side.
const BANNER_VERSION = "launch-night-2026-11-13";
const STORAGE_KEY = `cv_banner_dismissed:${BANNER_VERSION}`;

// Fri 13 Nov 2026, 1:00 PM WAT (Africa/Lagos, UTC+1). Encoded as a fixed
// UTC instant so browsers in any timezone count down to the same moment.
// 13:00 WAT = 12:00 UTC.
const EVENT_UTC_MS = Date.UTC(2026, 10, 13, 12, 0, 0);
// Event runs ~3h — after that we swap "Live now" for the past-event
// state so we don't keep pulsing an already-finished premiere.
const EVENT_DURATION_MS = 3 * 60 * 60 * 1000;

/**
 * Live countdown to ``EVENT_UTC_MS``. Ticks every second, snapping to the
 * next whole second so multiple mounts stay in phase. Returns the diff
 * broken into days/hours/minutes/seconds plus a status flag.
 */
function useCountdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Align the tick to the next wall-clock second so digits flip on the
    // second boundary rather than drifting by a few hundred ms.
    const drift = 1000 - (Date.now() % 1000);
    let interval;
    const align = setTimeout(() => {
      setNow(Date.now());
      interval = setInterval(() => setNow(Date.now()), 1000);
    }, drift);
    return () => {
      clearTimeout(align);
      if (interval) clearInterval(interval);
    };
  }, []);
  const diff = EVENT_UTC_MS - now;
  const isLive = diff <= 0 && now < EVENT_UTC_MS + EVENT_DURATION_MS;
  const isPast = now >= EVENT_UTC_MS + EVENT_DURATION_MS;
  const clamped = Math.max(diff, 0);
  const days = Math.floor(clamped / 86_400_000);
  const hours = Math.floor((clamped % 86_400_000) / 3_600_000);
  const minutes = Math.floor((clamped % 3_600_000) / 60_000);
  const seconds = Math.floor((clamped % 60_000) / 1000);
  return { days, hours, minutes, seconds, isLive, isPast };
}

const pad = (n) => String(n).padStart(2, "0");

/**
 * Dismissible top-of-page banner promoting the launch night. Meant to
 * ride above the Navbar on the Landing and Browse pages so returning
 * viewers see the event before they scroll. Dismissal is persisted in
 * localStorage keyed by ``BANNER_VERSION`` so a single "not-interested"
 * click doesn't need to be repeated on every visit.
 */
export default function LaunchBanner() {
  const [visible, setVisible] = useState(false);
  const { days, hours, minutes, seconds, isLive, isPast } = useCountdown();

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY);
      if (!dismissed) setVisible(true);
    } catch {
      // localStorage blocked (private tab, quota) — default to showing
      // the banner. Better to promote the event than to hide it forever.
      setVisible(true);
    }
  }, []);

  // While the banner is on screen we push the fixed Navbar and any
  // fixed floating banners down by the banner's height. Handled by a
  // body-class hook so we don't have to thread state through every page.
  useEffect(() => {
    if (visible) {
      document.body.classList.add("has-launch-banner");
      return () => document.body.classList.remove("has-launch-banner");
    }
    document.body.classList.remove("has-launch-banner");
    return undefined;
  }, [visible]);

  const dismiss = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setVisible(false);
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  if (!visible) return null;
  // Auto-retire the banner once the premiere is genuinely over — no
  // point promoting an event that's already happened. The countdown
  // hook flips ``isPast`` 3h after the start time.
  if (isPast) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 w-full" data-testid="launch-banner">
      <div
        className="relative overflow-hidden bg-gradient-to-r from-ink via-[#1a0f0a] to-ink border-b border-terracotta/30"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* Subtle radial glow — sells the "premiere" vibe without competing
            with page content. Pointer-events-none so it can't swallow the
            dismiss click on tiny viewports. */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(60% 200% at 20% 50%, rgba(195,106,74,0.28) 0%, rgba(195,106,74,0) 70%)," +
              "radial-gradient(60% 200% at 80% 50%, rgba(212,175,55,0.14) 0%, rgba(212,175,55,0) 70%)",
          }}
        />

        {/* Animated shimmer band — a slim vertical highlight that sweeps
            left→right every 4.5s. Sold as a premiere spotlight; kept
            subtle (~10% opacity peak) so it never fights the copy. */}
        <div
          className="absolute inset-y-0 -left-1/3 w-1/3 cv-banner-sweep pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,225,190,0.10) 45%, rgba(255,225,190,0.14) 55%, rgba(255,255,255,0) 100%)",
            mixBlendMode: "screen",
          }}
        />

        <Link
          to="/event"
          className="relative flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 group"
          data-testid="launch-banner-link"
        >
          <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-terracotta/20 border border-terracotta/40 shrink-0 cv-banner-tick-bob">
            {/* Pulsing ring — expands and fades outward every 2.2s so the
                icon feels like it's "broadcasting" the invite. */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full border border-terracotta/60 cv-banner-pulse"
            />
            <Ticket className="w-3.5 h-3.5 text-terracotta relative" />
          </span>

          <p className="flex-1 min-w-0 text-xs sm:text-sm leading-snug flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="uppercase tracking-widest text-terracotta font-bold hidden sm:inline">
              Launch Night
            </span>
            <span className="text-foreground/90">
              {isLive ? (
                <>
                  <strong className="text-foreground">Happening now</strong> — Silverbird Galleria VI. Come through.
                </>
              ) : (
                <>
                  Silverbird Galleria VI — <strong className="text-foreground hidden sm:inline">Fri 13 Nov 2026, 1:00 PM</strong>
                  <strong className="text-foreground sm:hidden">13 Nov, 1 PM</strong>
                </>
              )}
            </span>

            {/* Live countdown chip — mono digits so cells don't jitter as
                seconds flip. Days always visible; smaller units gated per
                breakpoint so the mobile banner stays single-line-ish. */}
            {isLive ? (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-terracotta/20 border border-terracotta/40 text-terracotta font-semibold cv-banner-pulse-live"
                data-testid="launch-banner-countdown"
                aria-live="polite"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-terracotta cv-banner-live-dot" />
                LIVE
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 rounded-full bg-terracotta/15 border border-terracotta/30 text-terracotta font-semibold tabular-nums tracking-tight"
                style={{ fontVariantNumeric: "tabular-nums" }}
                data-testid="launch-banner-countdown"
                aria-live="off"
              >
                <span className="text-[10px] uppercase tracking-widest opacity-70 hidden sm:inline">In</span>
                <span data-testid="cd-days"><strong>{days}</strong><span className="opacity-70">d</span></span>
                <span className="opacity-40">·</span>
                <span data-testid="cd-hours"><strong>{pad(hours)}</strong><span className="opacity-70">h</span></span>
                <span className="opacity-40">·</span>
                <span data-testid="cd-minutes"><strong>{pad(minutes)}</strong><span className="opacity-70">m</span></span>
                <span className="opacity-40 hidden sm:inline">·</span>
                <span data-testid="cd-seconds" className="hidden sm:inline"><strong>{pad(seconds)}</strong><span className="opacity-70">s</span></span>
              </span>
            )}
          </p>

          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-terracotta shrink-0 group-hover:gap-2 transition-all">
            <Calendar className="w-3.5 h-3.5" />
            Reserve seat
            <ArrowRight className="w-3.5 h-3.5 cv-banner-nudge" />
          </span>

          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss launch-night banner"
            className="relative shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-white/10 transition-colors"
            data-testid="launch-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </div>
  );
}
